import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// One shared tooltip for dense grids (attendance register, holiday calendar)
// where wrapping every cell in its own Radix Tooltip would cost a lot on each
// re-render. Any element with data-tip="…" gets a tooltip styled exactly like
// the shadcn TooltipContent (components/ui/tooltip.js). Discrete buttons use
// <Tip> instead.
const DELAY = 250;

export default function TipLayer() {
    const [tip, setTip] = useState(null); // { text, rect }
    const [pos, setPos] = useState(null);
    const boxRef = useRef(null);

    useEffect(() => {
        let timer = null;
        let current = null;

        const hide = () => {
            clearTimeout(timer);
            current = null;
            setTip(null);
        };
        const show = (el) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                const text = el.getAttribute('data-tip');
                if (text && el.isConnected) setTip({ text, rect: el.getBoundingClientRect() });
            }, DELAY);
        };
        const onOver = (e) => {
            const el = e.target && e.target.closest ? e.target.closest('[data-tip]') : null;
            if (el === current) return;
            current = el;
            setTip(null);
            if (el && el.getAttribute('data-tip')) show(el);
            else clearTimeout(timer);
        };
        const onFocus = (e) => {
            const el = e.target && e.target.closest ? e.target.closest('[data-tip]') : null;
            if (el && el.getAttribute('data-tip') && el.matches(':focus-visible')) { current = el; show(el); }
        };

        const onKey = (e) => { if (e.key === 'Escape') hide(); };

        document.addEventListener('pointerover', onOver);
        document.addEventListener('focusin', onFocus);
        document.addEventListener('focusout', hide);
        document.addEventListener('pointerdown', hide);
        document.addEventListener('keydown', onKey);
        window.addEventListener('scroll', hide, true);
        window.addEventListener('blur', hide);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('pointerover', onOver);
            document.removeEventListener('focusin', onFocus);
            document.removeEventListener('focusout', hide);
            document.removeEventListener('pointerdown', hide);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', hide, true);
            window.removeEventListener('blur', hide);
        };
    }, []);

    // Place above the target (flip below near the top edge), clamped to the viewport.
    useLayoutEffect(() => {
        if (!tip || !boxRef.current) { setPos(null); return; }
        const { rect } = tip;
        const box = boxRef.current.getBoundingClientRect();
        const gap = 6;
        const below = rect.top - box.height - gap < 8;
        const top = below ? rect.bottom + gap : rect.top - box.height - gap;
        const left = Math.min(Math.max(8, rect.left + rect.width / 2 - box.width / 2), window.innerWidth - box.width - 8);
        setPos({ top, left, side: below ? 'bottom' : 'top' });
    }, [tip]);

    if (!tip) return null;
    return createPortal(
        <div
            ref={boxRef}
            role="tooltip"
            data-side={pos?.side}
            style={{ position: 'fixed', top: pos ? pos.top : -9999, left: pos ? pos.left : -9999 }}
            className="pointer-events-none z-[1070] max-w-xs rounded-md bg-text-primary px-2 py-1 text-xs font-medium leading-snug text-background shadow-md animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1"
        >
            {tip.text}
        </div>,
        document.body
    );
}
